import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

// Redux + MUI + etc
import { ThemeProvider } from "@mui/material/styles";
import { configureStore } from "@reduxjs/toolkit";
import { SnackbarProvider } from "notistack";
import { Provider } from "react-redux";

// === Stytch B2B imports ===
import { StytchB2BProvider } from "@stytch/react/b2b";
import { StytchB2BUIClient } from "@stytch/vanilla-js/b2b";

// Your own modules
import AuthenticatedDashboard from "./AuthenticatedDashboard";
import ProtectedRoute from "./authentication/ProtectedRoute";
import generateMuiThemeDefaults from "./functions/generateMuiThemeDefaults";
import "./index.css";
import rootReducer from "./redux/reducers";
import CreditAuthorizationFormScreen from "./screens/CreditAuthorizationFormScreen";
import LoanApplicationFormScreen from "./screens/LoanApplicationFormScreen";
import LoginScreen from "./screens/LoginScreen";

// AG Grid
import { LicenseManager } from "ag-grid-enterprise";
import Authenticate from "./authentication/Authenticate";
import CreateAccountScreen from "./screens/CreateAccountScreen";
import CreditAuthPipelineScreen from "./screens/CreditAuthPipelineScreen";
import LoanApplicationPipelineScreen from "./screens/LoanApplicationPipelineScreen/LoanApplicationPipelineScreen";
import LoanDrilldownScreen from "./screens/LoanDrilldownScreen/LoanDrilldownScreen";
import LoanIntakePricingSizerScreen from "./screens/LoanIntakePricingSizerScreen";
import LoanPipelineScreen from "./screens/LoanPipelineScreen/LoanPipelineScreen";

import { useEffect } from "react";
import { useEnv } from "./hooks/useEnv";
import ChangelogScreen from "./screens/ChangelogScreen/ChangelogScreen";
import DevScreen from "./screens/DevScreen";
import DynamicFormScreen from "./screens/DynamicFormScreen";
import GlobalProcessingScreen from "./screens/GlobalProcessingScreen/GlobalProcessingScreen";
import LoanPipelineBoardScreen from "./screens/LoanPipelineBoardScreen";
import LoanQuickIntakeFormScreen from "./screens/LoanQuickIntakeFormScreen";
import LoanQuickApplicationFormWizardScreen from "./screens/LoanQuickApplicationFormWizardScreen";
import LoanQuickApplicationPipelineScreen from "./screens/LoanQuickApplicationPipelineScreen";
import LoanOfficerPipelineScreen from "./screens/LoanOfficerPipelineScreen";
import LoanAppsScreen from "./screens/LoanAppsScreen";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import BorrowerPortalUsersScreen from "./screens/BorrowerPortalUsersScreen";
import ProfileScreen from "./screens/ProfileScreen";
import EmailTemplateManagerScreen from "./screens/EmailTemplateManagerScreen";
import ExternalOrdersScreen from "./screens/ExternalOrdersScreen/ExternalOrdersScreen";
LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_ENTERPRISE_KEY);

// Create Redux store
const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }),
  devTools: process.env.NODE_ENV !== "production",
});

const stytchPublicToken = process.env.REACT_APP_STYTCH_PUBLIC_LIVE_TOKEN;

const stytchOptions = {
  cookieOptions: {
    opaqueTokenCookieName: "stytch_session",
    jwtCookieName: "stytch_session_jwt",
    path: "",
    availableToSubdomains: false,
    domain: "",
  },
};

// Create the B2B client
const stytchB2BClient = new StytchB2BUIClient(stytchPublicToken, stytchOptions);

const primaryColor = "#2962ff";

// ----------------------
// ContentRenderer FIRST
// ----------------------
const ContentRenderer = () => {
  const {
    isResfin,
    isResilender,
    isEastview,
    isLendmarq,
    isHandsomeHomebuyer,
    displayName,
  } = useEnv();

  useEffect(() => {
    // Decide title + favicon based on env
    // let title = "LO Portal";
    let title = `${displayName} | Portal`;
    let faviconPath = "%PUBLIC_URL%/favicon-generic.ico";

    if (isEastview) {
      // title = "Eastview | Portal";
      faviconPath = "%PUBLIC_URL%/eastview-favicon.png";
    } else if (isLendmarq) {
      // title = "Lendmarq | Portal";
      faviconPath = "%PUBLIC_URL%/favicon-lendmarq.ico";
    } else if (isResfin) {
      // title = "Resfin | Portal";
      faviconPath = "%PUBLIC_URL%/favicon-resfin.ico";
    } else if (isHandsomeHomebuyer) {
      // title = "Handsome Homebuyer | Portal";
      faviconPath = "%PUBLIC_URL%/favicons/handsome-homebuyer-favicon.png";
    } else if (isResilender) {
      // title = "Resilender | LO Portal";
      faviconPath = "%PUBLIC_URL%/favicon.ico";
    }

    document.title = title;
    // document.title = process.env.REACT_APP_PAGE_TITLE || title;

    // 2) Set favicon
    const head = document.querySelector("head");
    if (!head) return;

    // Try to find existing <link rel="icon">, otherwise create it
    let faviconLink =
      document.querySelector("link[rel='icon']") ||
      document.createElement("link");

    faviconLink.rel = "icon";
    // CRA doesn't expand %PUBLIC_URL% at runtime, only at build-time,
    // so build the path with process.env.PUBLIC_URL.
    const publicUrl = process.env.PUBLIC_URL || "";
    faviconLink.href = faviconPath.replace("%PUBLIC_URL%", publicUrl);

    // If it was newly created, append it
    if (!faviconLink.parentNode) {
      head.appendChild(faviconLink);
    }
  }, [isEastview, isResfin, isResilender]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/sign-up" element={<CreateAccountScreen />} />
      <Route
        path="/loan-application/*"
        element={<LoanApplicationFormScreen />}
      />
      <Route
        path="/loan-application/unbranded"
        element={<LoanApplicationFormScreen hideLogo />}
      />
      <Route
        path="/credit-auth/*"
        element={<CreditAuthorizationFormScreen />}
      />
      <Route
        path="/quick-app/*"
        element={<LoanQuickApplicationFormWizardScreen />}
      />
      {/* <Route
        path="/discovery-organization-handler"
        element={<DiscoveryOrganizationHandler />}
      /> */}

      <Route
        path="/rtl-sizer"
        element={<LoanIntakePricingSizerScreen showLogo />}
      />
      <Route
        path="/rtl-sizer/handsome-homebuyer"
        element={
          <LoanIntakePricingSizerScreen showLogo type={"handsomeHomebuyer"} />
        }
      />

      {/* Stytch magic link callback */}
      <Route path="/authenticate" element={<Authenticate />} />

      {/* Private/Protected Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AuthenticatedDashboard>
              <Routes>
                <Route path="/dev/*" element={<DevScreen />} />
                <Route path="/loans" element={<LoanOfficerPipelineScreen />} />
                {/* <Route path="/loans" element={<LoanPipelineScreen />} /> */}
                <Route path="/loans/legacy" element={<LoanPipelineScreen />} />
                <Route
                  path="/loans/:name?/*"
                  element={<LoanOfficerPipelineScreen />}
                />
                <Route
                  path="/email/templates/*"
                  element={<EmailTemplateManagerScreen />}
                />

                <Route
                  path="/users/:id?/*"
                  element={<BorrowerPortalUsersScreen />}
                />
                <Route path="/profile" element={<ProfileScreen />} />
                <Route path="/changelog" element={<ChangelogScreen />} />

                <Route path="/dynamic-form" element={<DynamicFormScreen />} />
                <Route
                  path="/loan-applications/:id?/*"
                  element={<LoanApplicationPipelineScreen />}
                />
                <Route path="/loan-apps/:id?/*" element={<LoanAppsScreen />} />
                <Route
                  path="/quick-applications/:id?/*"
                  element={<LoanQuickApplicationPipelineScreen />}
                />

                <Route
                  path="/credit-authorizations"
                  element={<CreditAuthPipelineScreen />}
                />
                <Route
                  path="/processing/*"
                  element={<GlobalProcessingScreen />}
                />

                <Route path="/loan/:id/*" element={<LoanDrilldownScreen />} />
                <Route
                  path="/loans/board"
                  element={<LoanPipelineBoardScreen />}
                />
                <Route
                  path="/loan/create"
                  element={<LoanQuickIntakeFormScreen />}
                />
                <Route
                  path="/loan/create/:type/*"
                  element={<LoanQuickIntakeFormScreen />}
                />
                <Route path="/orders/*" element={<ExternalOrdersScreen />} />
                {/* <Route
                  path="/orders/:type/*"
                  element={<ExternalOrdersScreen />}
                /> */}
                <Route
                  path="/loan/pricing-sizer"
                  element={<LoanIntakePricingSizerScreen />}
                />

                <Route path="/resources" element={<LoanPipelineScreen />} />
                <Route path="*" element={<Navigate to="/loans" />} />
              </Routes>
            </AuthenticatedDashboard>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

// ----------------------
// Mount the app
// ----------------------
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <StytchB2BProvider stytch={stytchB2BClient}>
    <Provider store={store}>
      <BrowserRouter>
        <SnackbarProvider maxSnack={5}>
          <ThemeProvider theme={generateMuiThemeDefaults({ primaryColor })}>
            <ContentRenderer />
          </ThemeProvider>
        </SnackbarProvider>
      </BrowserRouter>
    </Provider>
  </StytchB2BProvider>,
);
